import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

import type { ProjectAccessClient } from '@/backend/contracts/project-access-client';
import { db } from '@/firebase-config';
import {
  normalizeMemberWithProfile,
  type StoredProjectMember,
} from '@/features/admin/utils/member-records';
import type { ProjectMember, Role } from '@/features/auth/types';
import type { Personnel } from '@/features/personnel/types';
import type {
  InviteEmailUpdateRequest,
  ProjectAccessInvitation,
  ProjectAccessMember,
  ProjectAccessSnapshot,
  ProjectInvitationRead,
} from '@/features/project-access/project-access-types';
import {
  buildProjectDocPath,
  type ProjectScope,
} from '@/features/sync/project-scope';

type FirestoreDocumentSnapshot = {
  id: string;
  exists: () => boolean;
  data: () => unknown;
};

type FirestoreCollectionSnapshot = {
  docs: FirestoreDocumentSnapshot[];
};

function projectCollectionRef(scope: ProjectScope, collectionName: string) {
  return collection(db, buildProjectDocPath(scope), collectionName);
}

function projectSubcollectionDocRef(scope: ProjectScope, collectionName: string, docId: string) {
  return doc(db, buildProjectDocPath(scope), collectionName, docId);
}

function userDocRef(userId: string) {
  return doc(db, 'users', userId);
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function toProjectMemberStatus(value: unknown): ProjectMember['status'] {
  return value === 'active'
    || value === 'pending'
    || value === 'inactive'
    || value === 'removed'
    ? value
    : 'pending';
}

function toMillis(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return;
  const timestamp = value as { toMillis?: () => number; toDate?: () => Date; seconds?: number };
  if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
  if (typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
  if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
  return;
}

async function getUserProfileForMember(userId: string): Promise<Record<string, unknown> | null> {
  try {
    const snapshot = await getDoc(userDocRef(userId)) as FirestoreDocumentSnapshot;
    return snapshot.exists() ? asRecord(snapshot.data()) : null;
  } catch {
    return null;
  }
}

function profileDocToPersonnel(profileDoc: FirestoreDocumentSnapshot): Personnel {
  return {
    ...asRecord(profileDoc.data()),
    id: profileDoc.id,
  } as Personnel;
}

function roleDocToRole(roleDoc: FirestoreDocumentSnapshot): Role {
  const data = asRecord(roleDoc.data()) as Partial<Role>;
  return {
    ...data,
    id: data.id || roleDoc.id,
  } as Role;
}

function memberDocToStoredProjectMember(memberDoc: FirestoreDocumentSnapshot): StoredProjectMember {
  const data = asRecord(memberDoc.data());
  return {
    ...data,
    roleId: asString(data.roleId),
    status: toProjectMemberStatus(data.status),
    joinedAt: data.joinedAt ?? 0,
  };
}

function invitationDocToInvitation(invitationDoc: FirestoreDocumentSnapshot): ProjectAccessInvitation {
  const data = asRecord(invitationDoc.data());
  const projectPreferredEmail = asString(data.email);

  return {
    id: invitationDoc.id,
    email: projectPreferredEmail,
    name: asString(data.name, projectPreferredEmail || 'Invitation'),
    personnelId: asString(data.personnelId),
    roleId: asString(data.roleId),
    productionTitle: asString(data.productionTitle).trim() || undefined,
    status: asString(data.status, 'unknown'),
    sentAt: asString(data.sentAt) || undefined,
    tokenExpiresAt: toMillis(data.tokenExpiresAt),
  };
}

function invitationDocToRead(invitationDoc: FirestoreDocumentSnapshot): ProjectInvitationRead {
  const data = asRecord(invitationDoc.data());
  const projectPreferredEmail = asString(data.email);

  return {
    personnelId: asString(data.personnelId),
    email: projectPreferredEmail,
    name: asString(data.name, projectPreferredEmail),
    roleId: asString(data.roleId),
    productionTitle: asString(data.productionTitle).trim() || undefined,
  };
}

function inviteEmailRequestDocToRequest(requestDoc: FirestoreDocumentSnapshot): InviteEmailUpdateRequest | null {
  const data = asRecord(requestDoc.data());
  const status = asString(data.status, 'unknown');
  if (status !== 'open') return null;

  return {
    id: requestDoc.id,
    invitationId: asString(data.invitationId),
    requestedEmail: asString(data.requestedEmail),
    status,
    note: asString(data.note) || undefined,
    createdAt: toMillis(data.createdAt),
  };
}

export class FirebaseProjectAccessClient implements ProjectAccessClient {
  async listProjectAccess(scope: ProjectScope): Promise<ProjectAccessSnapshot> {
    const [
      membersSnapshot,
      rolesSnapshot,
      invitationsSnapshot,
      publicProfilesSnapshot,
      emailRequestsSnapshot,
    ] = await Promise.all([
      getDocs(projectCollectionRef(scope, 'members')),
      getDocs(projectCollectionRef(scope, 'roles')),
      getDocs(projectCollectionRef(scope, 'invitations')),
      getDocs(projectCollectionRef(scope, 'personnelPublic')),
      getDocs(projectCollectionRef(scope, 'inviteEmailUpdateRequests')),
    ]) as [
      FirestoreCollectionSnapshot,
      FirestoreCollectionSnapshot,
      FirestoreCollectionSnapshot,
      FirestoreCollectionSnapshot,
      FirestoreCollectionSnapshot,
    ];

    const personnel = publicProfilesSnapshot.docs.map(profileDocToPersonnel);
    const personnelById = new Map(personnel.map(person => [person.id, person]));

    const members = await Promise.all(membersSnapshot.docs.map(async memberDoc => {
      const memberData = memberDocToStoredProjectMember(memberDoc);
      const userProfile = await getUserProfileForMember(memberDoc.id);
      const normalized = normalizeMemberWithProfile(memberDoc.id, memberData, userProfile);
      const linkedPersonnel = normalized.personnelId
        ? personnelById.get(normalized.personnelId)
        : undefined;

      return {
        ...normalized,
        personnelName: linkedPersonnel?.name,
      } satisfies ProjectAccessMember;
    }));

    return {
      members,
      roles: rolesSnapshot.docs.map(roleDocToRole),
      invitations: invitationsSnapshot.docs.map(invitationDocToInvitation),
      inviteEmailUpdateRequests: emailRequestsSnapshot.docs.flatMap(requestDoc => {
        const request = inviteEmailRequestDocToRequest(requestDoc);
        return request ? [request] : [];
      }),
      personnel,
    };
  }

  async getProjectInvitation(scope: ProjectScope, invitationId: string): Promise<ProjectInvitationRead | null> {
    const invitationSnapshot = await getDoc(
      projectSubcollectionDocRef(scope, 'invitations', invitationId),
    ) as FirestoreDocumentSnapshot;

    if (!invitationSnapshot.exists()) {
      return null;
    }

    return invitationDocToRead(invitationSnapshot);
  }
}
